import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { uploadFileToGCS } from '../utils/gcsUpload';
import { Plus, MagnifyingGlass, Download, Trash, Eye, Paperclip, X, EnvelopeSimple, PaperPlaneTilt, Funnel, PencilSimple } from '@phosphor-icons/react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default column widths
const DEFAULT_COLUMN_WIDTHS = {
  correspondence_nr: 140,
  title: 250,
  status: 100,
  discipline: 100,
  from: 140,
  to: 140,
  due_date: 100,
  actions: 80
};

// Sort icon component (outside main component to avoid re-creation)
const SortIcon = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="h-3 w-3 text-gray-400 ml-1" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="h-3 w-3 text-blue-600 ml-1" />
    : <ArrowDown className="h-3 w-3 text-blue-600 ml-1" />;
};

const Correspondence = ({ user, selectedProject }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [lateOnly, setLateOnly] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newItemFile, setNewItemFile] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);  // null = create mode; id = edit mode
  const fileInputRef = useRef(null);

  // Only Document Controllers and System Admins may edit correspondence
  const canEdit = !!user && (
    user.is_super_admin ||
    ['admin', 'system admin', 'document controller'].includes((user.role || '').toLowerCase())
  );
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('correspondence_nr');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Resizable columns state
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const resizingRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  
  const [newItem, setNewItem] = useState({
    correspondence_nr: '',
    title: '',
    sender_reference: '',
    status: 'new',
    subclass: '',
    issue_code: '',
    discipline: '',
    correspondence_type: '',
    requested_priority: '',
    assigned_priority: '',
    sent_by_company: '',
    sent_by_department: '',
    sent_by_person: '',
    sent_to_company: '',
    sent_to_department: '',
    sent_to_person: '',
    purchase_order: '',
    due_date: '',
    issued_date: '',
    direction: 'incoming'
  });

  useEffect(() => {
    fetchItems();
  }, [activeTab, searchTerm, filterStatus, filterDiscipline, outstandingOnly, lateOnly, selectedProject]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (activeTab === 'inbox') params.append('direction', 'incoming');
      else if (activeTab === 'outgoing') params.append('direction', 'outgoing');
      
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus) params.append('status', filterStatus);
      if (filterDiscipline) params.append('discipline', filterDiscipline);
      if (outstandingOnly) params.append('outstanding_only', 'true');
      if (lateOnly) params.append('late_only', 'true');
      if (selectedProject) params.append('project_id', selectedProject);
      
      const response = await axios.get(`${API}/correspondence?${params.toString()}`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch correspondence');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (item) => {
    if (!canEdit) {
      toast.error('Only Document Controllers and System Admins can edit correspondence');
      return;
    }
    setEditingItemId(item.id);
    setNewItem({
      correspondence_nr: item.reference_number || '',
      title: item.subject || '',
      sender_reference: item.sender_reference || '',
      status: item.status || 'new',
      subclass: item.subclass || '',
      issue_code: item.issue_code || '',
      discipline: item.category || '',
      correspondence_type: item.description || '',
      requested_priority: item.priority || '',
      assigned_priority: item.assigned_priority || '',
      sent_by_company: item.from_company || '',
      sent_by_department: item.from_department || '',
      sent_by_person: item.from_contact || '',
      sent_to_company: item.to_company || '',
      sent_to_department: item.to_department || '',
      sent_to_person: item.to_contact || '',
      purchase_order: item.purchase_order || '',
      due_date: item.due_date ? String(item.due_date).slice(0, 10) : '',
      issued_date: item.date_sent ? String(item.date_sent).slice(0, 10) : '',
      direction: item.correspondence_type || 'incoming',
    });
    setNewItemFile(null);
    setCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newItem.correspondence_nr || !newItem.title) {
      toast.error('Correspondence Nr. and Title are required');
      return;
    }
    
    try {
      // Map frontend fields to backend expected fields
      const payload = {
        reference_number: newItem.correspondence_nr,
        subject: newItem.title,
        correspondence_type: newItem.direction || 'incoming',
        from_company: newItem.sent_by_company,
        from_contact: newItem.sent_by_person,
        to_company: newItem.sent_to_company,
        to_contact: newItem.sent_to_person,
        date_sent: newItem.issued_date || null,
        due_date: newItem.due_date || null,
        status: newItem.status || 'new',
        priority: newItem.requested_priority || 'normal',
        category: newItem.discipline,
        description: newItem.correspondence_type
      };

      let savedItem;
      if (editingItemId) {
        const response = await axios.put(`${API}/correspondence/${editingItemId}`, payload);
        savedItem = response.data;
      } else {
        const response = await axios.post(`${API}/correspondence`, payload);
        savedItem = response.data;
      }
      
      // Upload file attachment if selected (direct-to-GCS via signed URL)
      if (newItemFile) {
        const info = await uploadFileToGCS(newItemFile, 'correspondence');
        await axios.post(`${API}/uploads/register-item-attachment`, {
          item_id: savedItem.id,
          item_type: 'correspondence',
          gcs_path: info.gcs_path,
          original_filename: info.original_filename,
          file_size: info.file_size,
          content_type: info.content_type,
        });
      }
      
      toast.success(editingItemId ? 'Correspondence updated successfully' : 'Correspondence created successfully');
      setCreateOpen(false);
      setEditingItemId(null);
      setNewItemFile(null);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('Save correspondence error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || error.message || (editingItemId ? 'Failed to update correspondence' : 'Failed to create correspondence'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this correspondence?')) return;
    
    try {
      await axios.delete(`${API}/correspondence/${id}`);
      toast.success('Correspondence deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete correspondence');
    }
  };

  const handleView = async (item) => {
    setSelectedItem(item);
    setViewOpen(true);
    // Fetch attachments
    try {
      const response = await axios.get(`${API}/correspondence/${item.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      setAttachments([]);
    }
  };

  const handleUploadAttachment = async (file) => {
    if (!selectedItem || !file) return;
    
    setUploading(true);
    try {
      const info = await uploadFileToGCS(file, 'correspondence');
      await axios.post(`${API}/uploads/register-item-attachment`, {
        item_id: selectedItem.id,
        item_type: 'correspondence',
        gcs_path: info.gcs_path,
        original_filename: info.original_filename,
        file_size: info.file_size,
        content_type: info.content_type,
      });
      toast.success('Attachment uploaded');
      const response = await axios.get(`${API}/correspondence/${selectedItem.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(`${API}/item-attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/correspondence/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'correspondence_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Correspondence exported to Excel');
    } catch (error) {
      toast.error('Failed to export correspondence');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(`${API}/item-attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      const response = await axios.get(`${API}/correspondence/${selectedItem.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await axios.put(`${API}/correspondence/${selectedItem.id}`, { status });
      toast.success('Status updated');
      fetchItems();
      setSelectedItem({ ...selectedItem, status });
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setNewItem({
      correspondence_nr: '',
      title: '',
      sender_reference: '',
      status: 'new',
      subclass: '',
      issue_code: '',
      discipline: '',
      correspondence_type: '',
      requested_priority: '',
      assigned_priority: '',
      sent_by_company: '',
      sent_by_department: '',
      sent_by_person: '',
      sent_to_company: '',
      sent_to_department: '',
      sent_to_person: '',
      purchase_order: '',
      due_date: '',
      issued_date: '',
      direction: 'incoming'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-orange-100 text-orange-800',
      closed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(String(dateStr).endsWith('Z') || String(dateStr).includes('+') ? dateStr : dateStr + 'Z').toLocaleDateString();
  };

  // Sorting handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sorted items
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      // Handle 'from' and 'to' virtual columns
      if (sortColumn === 'from') {
        aVal = a.sent_by_company || a.sent_by_person || '';
        bVal = b.sent_by_company || b.sent_by_person || '';
      } else if (sortColumn === 'to') {
        aVal = a.sent_to_company || a.sent_to_person || '';
        bVal = b.sent_to_company || b.sent_to_person || '';
      }

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (sortColumn === 'due_date' || sortColumn === 'issued_date' || sortColumn === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, sortColumn, sortDirection]);

  // Column resize handlers
  const startResize = useCallback((columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = columnKey;
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[columnKey] || 150;

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const diff = moveEvent.clientX - startXRef.current;
      const newWidth = Math.max(60, startWidthRef.current + diff);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current]: newWidth }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // Render resizable header
  const renderResizableHeader = (column, label, sortable = true) => (
    <th 
      key={column}
      className="text-left p-3 font-semibold text-gray-700 relative group"
      style={{ width: columnWidths[column], minWidth: columnWidths[column] }}
    >
      {sortable ? (
        <button 
          onClick={() => handleSort(column)} 
          className="flex items-center hover:text-gray-900"
        >
          {label} <SortIcon column={column} sortColumn={sortColumn} sortDirection={sortDirection} />
        </button>
      ) : (
        <span>{label}</span>
      )}
      <div
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
        onMouseDown={(e) => startResize(column, e)}
      />
    </th>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="correspondence-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-1">
            Correspondence
          </h1>
          <p className="text-gray-500">Manage incoming and outgoing correspondence</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={handleExportExcel}
            className="rounded-xl h-11 px-4"
          >
            <Download size={18} className="mr-2" />
            Export Excel
          </Button>
          
          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) { setEditingItemId(null); resetForm(); setNewItemFile(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 rounded-xl h-11 px-5" data-testid="create-correspondence-btn">
                <Plus size={18} className="mr-2" />
                New Correspondence
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl">{editingItemId ? 'Edit Correspondence' : 'Create New Correspondence'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Correspondence Nr.*</Label>
                  <Input
                    value={newItem.correspondence_nr}
                    onChange={(e) => setNewItem({ ...newItem, correspondence_nr: e.target.value })}
                    placeholder="e.g., CORR-2025-001"
                    required
                  />
                </div>
                <div>
                  <Label>Direction*</Label>
                  <Select value={newItem.direction} onValueChange={(val) => setNewItem({ ...newItem, direction: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="incoming">Incoming</SelectItem>
                      <SelectItem value="outgoing">Outgoing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Title*</Label>
                <Input
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  placeholder="Correspondence title"
                  required
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={newItem.status} onValueChange={(val) => setNewItem({ ...newItem, status: val })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Discipline</Label>
                  <Input
                    value={newItem.discipline}
                    onChange={(e) => setNewItem({ ...newItem, discipline: e.target.value })}
                    placeholder="e.g., Mechanical"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Input
                    value={newItem.correspondence_type}
                    onChange={(e) => setNewItem({ ...newItem, correspondence_type: e.target.value })}
                    placeholder="e.g., Letter, RFI"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Issue Code</Label>
                  <Input
                    value={newItem.issue_code}
                    onChange={(e) => setNewItem({ ...newItem, issue_code: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Sender Reference</Label>
                  <Input
                    value={newItem.sender_reference}
                    onChange={(e) => setNewItem({ ...newItem, sender_reference: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Sender Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Company</Label>
                    <Input
                      value={newItem.sent_by_company}
                      onChange={(e) => setNewItem({ ...newItem, sent_by_company: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={newItem.sent_by_department}
                      onChange={(e) => setNewItem({ ...newItem, sent_by_department: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Person</Label>
                    <Input
                      value={newItem.sent_by_person}
                      onChange={(e) => setNewItem({ ...newItem, sent_by_person: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Recipient Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Company</Label>
                    <Input
                      value={newItem.sent_to_company}
                      onChange={(e) => setNewItem({ ...newItem, sent_to_company: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={newItem.sent_to_department}
                      onChange={(e) => setNewItem({ ...newItem, sent_to_department: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Person</Label>
                    <Input
                      value={newItem.sent_to_person}
                      onChange={(e) => setNewItem({ ...newItem, sent_to_person: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={newItem.due_date}
                    onChange={(e) => setNewItem({ ...newItem, due_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Issued Date</Label>
                  <Input
                    type="date"
                    value={newItem.issued_date}
                    onChange={(e) => setNewItem({ ...newItem, issued_date: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Requested Priority</Label>
                  <Select value={newItem.requested_priority} onValueChange={(val) => setNewItem({ ...newItem, requested_priority: val })}>
                    <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assigned Priority</Label>
                  <Select value={newItem.assigned_priority} onValueChange={(val) => setNewItem({ ...newItem, assigned_priority: val })}>
                    <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* File Attachment */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Paperclip size={16} />
                  Document Attachment (Optional)
                </h4>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 hover:border-blue-400 transition-colors">
                  <Input
                    type="file"
                    onChange={(e) => setNewItemFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.jpg,.jpeg,.png"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Supported: PDF, DOC, DOCX, XLS, XLSX, ZIP, JPG, PNG
                  </p>
                  {newItemFile && (
                    <div className="mt-3 p-2 bg-blue-50 rounded-lg flex items-center justify-between">
                      <span className="text-sm text-blue-800 truncate flex-1">
                        {newItemFile.name}
                      </span>
                      <span className="text-xs text-blue-600 ml-2">
                        {(newItemFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setNewItemFile(null)}
                        className="ml-2 h-6 w-6 p-0 text-blue-600 hover:text-red-500"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              <Button type="submit" className="w-full">{editingItemId ? 'Save Changes' : 'Create Correspondence'}</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="inbox" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <EnvelopeSimple size={16} />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="search" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <MagnifyingGlass size={16} />
            Search
          </TabsTrigger>
          <TabsTrigger value="outgoing" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <PaperPlaneTilt size={16} />
            Outgoing
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 shadow-apple-sm">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by number, title, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 rounded-xl border-gray-200"
            />
          </div>
          <Select value={filterStatus || "all"} onValueChange={(val) => setFilterStatus(val === "all" ? "" : val)}>
            <SelectTrigger className="w-40 h-10 rounded-xl border-gray-200">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Discipline"
            value={filterDiscipline}
            onChange={(e) => setFilterDiscipline(e.target.value)}
            className="w-40 h-10 rounded-xl border-gray-200"
          />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <Checkbox checked={outstandingOnly} onCheckedChange={setOutstandingOnly} />
            Outstanding only
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <Checkbox checked={lateOnly} onCheckedChange={setLateOnly} />
            Late only
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-apple-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '900px' }}>
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {renderResizableHeader('correspondence_nr', 'Corr. Nr.')}
                {renderResizableHeader('title', 'Title')}
                {renderResizableHeader('status', 'Status')}
                {renderResizableHeader('discipline', 'Discipline')}
                {renderResizableHeader('from', 'From')}
                {renderResizableHeader('to', 'To')}
                {renderResizableHeader('due_date', 'Due Date')}
                {renderResizableHeader('actions', 'Actions', false)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center py-12">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-12 text-gray-500">No correspondence found</td>
              </tr>
            ) : (
              sortedItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-3 font-medium text-gray-900" style={{ width: columnWidths.correspondence_nr }}>
                    {item.reference_number || item.correspondence_nr || '-'}
                  </td>
                  <td className="p-3 truncate text-gray-700" style={{ width: columnWidths.title, maxWidth: columnWidths.title }} title={item.subject || item.title}>
                    {item.subject || item.title || '-'}
                  </td>
                  <td className="p-3" style={{ width: columnWidths.status }}>
                    <span className={`text-xs px-2.5 py-1 rounded-lg font-medium ${getStatusColor(item.status)}`}>
                      {(item.status || 'new').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-3 text-gray-600" style={{ width: columnWidths.discipline }}>
                    {item.category || item.discipline || '-'}
                  </td>
                  <td className="p-3 text-gray-600" style={{ width: columnWidths.from }}>
                    {item.from_company || item.from_contact || item.sent_by_company || item.sent_by_person || '-'}
                  </td>
                  <td className="p-3 text-gray-600" style={{ width: columnWidths.to }}>
                    {item.to_company || item.to_contact || item.sent_to_company || item.sent_to_person || '-'}
                  </td>
                  <td className="p-3 text-gray-600" style={{ width: columnWidths.due_date }}>{formatDate(item.due_date)}</td>
                  <td className="p-3" style={{ width: columnWidths.actions }}>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleView(item)} title="View" className="h-8 w-8 p-0 rounded-lg hover:bg-gray-100">
                        <Eye size={16} className="text-gray-500" />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => handleStartEdit(item)} title="Edit" className="h-8 w-8 p-0 rounded-lg hover:bg-blue-50" data-testid={`edit-correspondence-${item.id}`}>
                          <PencilSimple size={16} className="text-blue-600" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} title="Delete" className="h-8 w-8 p-0 rounded-lg hover:bg-red-50">
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Correspondence Details</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Correspondence Nr.</Label>
                  <p className="font-medium">{selectedItem.correspondence_nr}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-sm ${getStatusColor(selectedItem.status)}`}>
                      {selectedItem.status?.replace('_', ' ')}
                    </span>
                    <Select value={selectedItem.status} onValueChange={handleUpdateStatus}>
                      <SelectTrigger className="w-32 h-7">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Title</Label>
                <p className="font-medium">{selectedItem.subject || selectedItem.title || '-'}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-muted-foreground">Discipline</Label>
                  <p>{selectedItem.category || selectedItem.discipline || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p>{selectedItem.correspondence_type || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Issue Code</Label>
                  <p>{selectedItem.issue_code || '-'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-sm p-3">
                  <h4 className="font-medium mb-2">From</h4>
                  <p className="text-sm">{selectedItem.from_company || selectedItem.sent_by_company || '-'}</p>
                  <p className="text-sm text-muted-foreground">{selectedItem.sent_by_department}</p>
                  <p className="text-sm text-muted-foreground">{selectedItem.from_contact || selectedItem.sent_by_person}</p>
                </div>
                <div className="border rounded-sm p-3">
                  <h4 className="font-medium mb-2">To</h4>
                  <p className="text-sm">{selectedItem.to_company || selectedItem.sent_to_company || '-'}</p>
                  <p className="text-sm text-muted-foreground">{selectedItem.sent_to_department}</p>
                  <p className="text-sm text-muted-foreground">{selectedItem.to_contact || selectedItem.sent_to_person}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Due Date</Label>
                  <p>{formatDate(selectedItem.due_date)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Issued Date</Label>
                  <p>{formatDate(selectedItem.date_sent || selectedItem.issued_date)}</p>
                </div>
              </div>
              
              {/* Attachments */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Paperclip size={16} />
                    Attachments ({attachments.length})
                  </h4>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleUploadAttachment(e.target.files[0]);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      <Plus size={14} className="mr-1" />
                      Add Attachment
                    </Button>
                  </div>
                </div>
                
                {attachments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No attachments</p>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((att) => (
                      <div key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-sm">
                        <span className="text-sm truncate flex-1">{att.original_filename}</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleDownloadAttachment(att)}>
                            <Download size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteAttachment(att.id)} className="text-red-500">
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Correspondence;
